import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCheck, FiTrendingDown, FiTrendingUp, FiAlertTriangle, FiShield, FiTarget, FiZap } = FiIcons;

const BeforeAfterSection = () => {
  const beforeItems = [
    { icon: FiX, text: "No system", color: "red" },
    { icon: FiTrendingDown, text: "Leads lost", color: "red" },
    { icon: FiAlertTriangle, text: "Manual follow-ups", color: "red" },
    { icon: FiX, text: "Unprofessional brand image", color: "red" },
    { icon: FiTrendingDown, text: "Heavy dependency on agencies", color: "red" },
    { icon: FiX, text: "No analytics", color: "red" },
    { icon: FiAlertTriangle, text: "High stress", color: "red" }
  ];

  const afterItems = [
    { icon: FiCheck, text: "Automated funnels", color: "green" },
    { icon: FiZap, text: "WhatsApp AI nurturing", color: "green" },
    { icon: FiTarget, text: "CRM tracking", color: "green" },
    { icon: FiTrendingUp, text: "High conversions", color: "green" },
    { icon: FiShield, text: "Professional website", color: "green" },
    { icon: FiCheck, text: "Automated sales", color: "green" },
    { icon: FiCheck, text: "Peace of mind", color: "green" }
  ];

  const valueItems = [
    "All-in-one system",
    "Unlimited funnels",
    "WhatsApp automation",
    "Email + SMS marketing",
    "Webinar system",
    "Community + editing tools",
    "24/7 support"
  ];

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='0.1' fill-rule='evenodd'%3E%3Cpath d='m0 40l40-40h-40v40z'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Before vs After Using 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-green-500"> FunnelsDone</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See the dramatic transformation that happens when you switch from chaos to systematic growth
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
          {/* Before Column */}
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <SafeIcon icon={FiX} className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-2xl font-bold text-red-600">❌ Before FunnelsDone</h3>
              <p className="text-gray-600 mt-2">The painful reality</p>
            </div>

            <div className="space-y-4">
              {beforeItems.map((item, index) => (
                <motion.div
                  key={index}
                  className="flex items-center space-x-4 p-4 bg-red-50 border border-red-200 rounded-xl"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <SafeIcon icon={item.icon} className="w-6 h-6 text-red-500 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* After Column */}
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-green-600">✔ After FunnelsDone</h3>
              <p className="text-gray-600 mt-2">The transformation</p>
            </div>

            <div className="space-y-4">
              {afterItems.map((item, index) => (
                <motion.div
                  key={index}
                  className="flex items-center space-x-4 p-4 bg-green-50 border border-green-200 rounded-xl"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <SafeIcon icon={item.icon} className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* What You Get Column */}
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <SafeIcon icon={FiTarget} className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-2xl font-bold text-blue-600">🎁 What You Get</h3>
              <p className="text-gray-600 mt-2">Complete package</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-2xl p-6">
              <div className="space-y-4">
                {valueItems.map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center space-x-4"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-gray-800 font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-white rounded-xl border border-blue-200">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">₹2,97,000</div>
                  <div className="text-sm text-gray-600 line-through mb-2">Individual Tool Cost/Year</div>
                  <div className="text-lg font-bold text-green-600">vs FunnelsDone All-in-One</div>
                  <div className="text-2xl font-bold text-purple-600 mt-2">Save 80%+ Annually</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Ready to Make the Switch?</h3>
            <p className="text-lg mb-6 opacity-90">Join hundreds of MSMEs who've transformed their business operations</p>
            <a 
              href="https://funnelsdone.com/cash" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-bold text-lg rounded-full hover:bg-gray-100 transition-all duration-300"
            >
              👉 Book Your Transformation Call
              <SafeIcon icon={FiTarget} className="ml-2 w-5 h-5" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BeforeAfterSection;